import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  AdminLayout,
  Banner,
  ConfirmDialog,
  FieldGroup,
  IconButton,
  PageHeader,
  SaveBar,
  Section,
  SideNav,
  StatusChip,
  WS_COLOR,
  WorkspaceThemeProvider,
  icons,
  type SaveState,
} from '@platform/ui-kit/workspace';
import { api, type AirFitmentCriteria, type AirPlatformCriterion } from '../../../api';

/**
 * Strategic Intelligence → Platform Fitment Criteria, as a Decision Workspace screen.
 *
 * PILOT, shown with `?ui=workspace` next to the ported original in `PlatformFitmentCriteria.tsx`
 * (which stays the default until the owner approves this one: CLAUDE.md 1.2). It uses layout 10,
 * Administration: section navigation, grouped fields, a fixed save bar.
 *
 * WHAT IS UNCHANGED: every API call and payload, the three tabs and their names, the tab in the
 * URL hash (`#capability`, `#readiness`, `#weighting`), the one lock across all three, the
 * confirmation before unlocking when analyses exist, the criteria editor's save / add / remove,
 * all the copy, and the rule that only the profile and weighting are sent from the readiness and
 * weighting tabs (the criterion weights are saved by the capabilities tab alone).
 *
 * WHAT CHANGES, by the design system and nothing else: the page frame, one header treatment, the
 * lock as a status chip plus a button, messages as banners, save state in the fixed bar, Lucide
 * icons instead of emoji, and the browser `confirm()` replaced by the standard confirm dialog.
 */

type TabKey = 'cap' | 'rdy' | 'wt';

const HASH: Record<TabKey, string> = { cap: 'capability', rdy: 'readiness', wt: 'weighting' };

const TABS = [
  { key: 'cap', label: 'Platform Capabilities' },
  { key: 'rdy', label: 'Organizational Readiness' },
  { key: 'wt', label: 'Weighting' },
] as const;

function tabFromHash(): TabKey {
  const h = (window.location.hash || '').replace('#', '');
  if (h === 'readiness') return 'rdy';
  if (h === 'weighting') return 'wt';
  return 'cap';
}

export function PlatformFitmentCriteriaWorkspace() {
  return (
    <WorkspaceThemeProvider baseline={false}>
      <Screen />
    </WorkspaceThemeProvider>
  );
}

function Screen() {
  const [data, setData] = useState<AirFitmentCriteria | null>(null);
  const [criteria, setCriteria] = useState<AirPlatformCriterion[]>([]);
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const [loading, setLoading] = useState(true);
  const [save, setSave] = useState<{ state: SaveState; message?: string }>({ state: 'clean' });
  const [error, setError] = useState<string>();
  const [confirmUnlock, setConfirmUnlock] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const loadCriteria = () =>
    api.aiReadiness
      .getPlatformCriteria()
      .then((d) => setCriteria(d.criteria))
      .catch((e: Error) => setError(e?.message || 'Could not load the criteria.'));

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.aiReadiness
        .getFitmentCriteria()
        .then(setData)
        .catch((e: Error) => setError(e?.message || 'Could not load the criteria.')),
      loadCriteria(),
    ]).finally(() => setLoading(false));
  }, []);

  const pickTab = (key: string) => {
    const t = key as TabKey;
    setTab(t);
    setSave({ state: 'clean' });
    window.history.replaceState(null, '', `#${HASH[t]}`);
  };

  if (loading) return <Typography sx={{ color: WS_COLOR.textMuted }}>Loading…</Typography>;
  if (!data) return <Banner tone="error">{error || 'Unavailable.'}</Banner>;

  const locked = data.locked;
  const wtCap = data.weightCapability;
  const wtRdy = 100 - wtCap;

  const edit = (next: AirFitmentCriteria) => {
    setData(next);
    setSave({ state: 'dirty' });
  };

  const run = async (work: () => Promise<unknown>, done: string, failed: string) => {
    setSave({ state: 'saving' });
    setError(undefined);
    try {
      await work();
      setSave({ state: 'saved', message: done });
    } catch (e) {
      setSave({ state: 'error', message: (e as Error).message || failed });
    }
  };

  /* Only the profile and the weighting. The criterion WEIGHTS belong to the Platform
     Capabilities tab and are saved by its own call: sending them here wrote platform_criteria
     ids into a column keyed to dimensions and broke saving the client profile. */
  const saveProfile = () =>
    run(
      () =>
        api.aiReadiness.saveFitmentCriteria({
          clientProfile: data.clientProfile,
          weightCapability: data.weightCapability,
        }),
      'Fitment criteria saved.',
      'Could not save.',
    );

  const saveCriteria = () =>
    run(
      () =>
        api.aiReadiness.savePlatformCriteria(
          criteria.map((c) => ({ id: c.id, selected: c.is_selected, weight: c.weight })),
        ),
      'Criteria saved.',
      'Could not save.',
    );

  const doToggleLock = async () => {
    try {
      const r = await api.aiReadiness.toggleFitmentLock();
      setData({ ...data, locked: r.locked });
      setSave({
        state: 'saved',
        message: r.locked
          ? 'Criteria locked as the standard for all AI Platform analyses.'
          : 'Criteria unlocked for editing.',
      });
    } catch (e) {
      setError((e as Error).message || 'Could not change the lock.');
    }
  };

  /* The source confirms before UNLOCKING when analyses already exist, and says how many,
     because unlocking is what lets the standard move underneath them. */
  const toggleLock = () => {
    if (locked && data.analyzedCount > 0) setConfirmUnlock(true);
    else void doToggleLock();
  };

  const setRow = (id: number, patch: Partial<AirPlatformCriterion>) => {
    setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));
    setSave({ state: 'dirty' });
  };

  const addCriterion = async () => {
    const name = newName.trim();
    if (!name) {
      setError('Enter an aspect name');
      return;
    }
    setError(undefined);
    try {
      await api.aiReadiness.addPlatformCriterion(name, newDescription.trim());
      setNewName('');
      setNewDescription('');
      await loadCriteria();
    } catch (e) {
      setError((e as Error).message || 'Could not add the aspect.');
    }
  };

  const removeCriterion = async (id: number) => {
    try {
      await api.aiReadiness.deletePlatformCriterion(id);
      setCriteria((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      setError((e as Error).message || 'Could not remove the aspect.');
    }
  };

  const saveLabel =
    tab === 'cap' ? 'Save criteria' : tab === 'rdy' ? 'Save client profile' : 'Save weighting';
  const lockedNote =
    tab === 'rdy'
      ? 'Unlock above to edit the profile.'
      : tab === 'wt'
        ? 'Unlock above to change the weighting.'
        : 'Locked. Unlock above to edit.';

  return (
    <>
      <AdminLayout
        header={
          <PageHeader
            title="Platform Fitment Criteria"
            subtitle="The standard capability criteria, organizational readiness and weighting applied to every AI Platform analysis."
            meta={
              <StatusChip
                tone={locked ? 'success' : 'warning'}
                label={locked ? 'Locked' : 'Unlocked'}
              />
            }
            actions={
              <Button
                variant="tonal"
                startIcon={locked ? <icons.Unlock size={18} /> : <icons.Lock size={18} />}
                onClick={toggleLock}
              >
                {locked ? 'Unlock to edit' : 'Lock as standard'}
              </Button>
            }
          />
        }
        nav={<SideNav label="Criteria sections" items={TABS} value={tab} onChange={pickTab} />}
        footer={
          <SaveBar
            state={locked ? 'clean' : save.state}
            message={locked ? (save.state === 'saved' ? save.message : lockedNote) : save.message}
          >
            <Button
              variant="contained"
              disabled={locked || save.state === 'saving'}
              onClick={() => void (tab === 'cap' ? saveCriteria() : saveProfile())}
            >
              {save.state === 'saving' ? 'Saving…' : saveLabel}
            </Button>
          </SaveBar>
        }
      >
        <Box sx={{ display: 'grid', gap: 3, mb: 5 }}>
          {data.analyzedCount > 0 ? (
            <Banner tone="warning">
              AI Platform analysis has already been executed for{' '}
              <strong>{data.analyzedCount}</strong> opportunit
              {data.analyzedCount === 1 ? 'y' : 'ies'}. Changing anything here will{' '}
              <strong>not</strong> update those analyses; you would need to re-run them.
            </Banner>
          ) : null}
          <Banner tone={locked ? 'success' : 'info'}>
            {locked
              ? 'Locked: the fixed standard for all analyses (capability, readiness and weighting).'
              : 'Unlocked: editable. Lock to use as the standard.'}
          </Banner>
          {error ? (
            <Banner tone="error" onDismiss={() => setError(undefined)}>
              {error}
            </Banner>
          ) : null}
        </Box>

        {tab === 'cap' ? (
          <Section
            id="fc-cap"
            title="Platform Capabilities"
            description={
              <>
                Choose which aspects matter when selecting an AI platform and how much each one
                weighs. The <strong>AI Platform Fit</strong> analysis uses these selected, weighted
                criteria, together with the opportunity&apos;s scoring Q&amp;A and documents, so fit
                scores reflect real selection trade-offs (cost, ease of learning, deployment speed,
                lock-in, …), not just raw capability.
              </>
            }
          >
            <Box
              component="ul"
              aria-label="Platform selection criteria"
              sx={{ listStyle: 'none', m: 0, p: 0 }}
            >
              {criteria.map((c) => (
                <Box
                  component="li"
                  key={c.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 3,
                    minHeight: 56,
                    borderTop: `1px solid ${WS_COLOR.border}`,
                    // An unselected aspect is dimmed, not hidden, as in the source. Dimmed by ink and
                    // weight, not `opacity`: opacity would take muted text below 4.5:1.
                    flexWrap: 'wrap',
                  }}
                >
                  <Checkbox
                    disabled={locked}
                    checked={c.is_selected}
                    onChange={(e) => setRow(c.id, { is_selected: e.target.checked })}
                    inputProps={{ 'aria-label': `Use ${c.name}` }}
                  />
                  <Box sx={{ flex: '1 1 220px', minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: c.is_selected ? 600 : 400,
                        color: c.is_selected ? WS_COLOR.text : WS_COLOR.textMuted,
                      }}
                    >
                      {c.name}
                    </Typography>
                    {c.description ? (
                      <Typography variant="caption" sx={{ color: WS_COLOR.textMuted }}>
                        {c.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Slider
                    disabled={locked}
                    min={1}
                    max={5}
                    step={1}
                    marks
                    value={c.weight}
                    onChange={(_, v) => setRow(c.id, { weight: v as number })}
                    aria-label={`Weight for ${c.name}, 1 to 5`}
                    sx={{ width: 170, flexShrink: 0 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ width: 20, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {c.weight}
                  </Typography>
                  <IconButton
                    icon={icons.Trash2}
                    label={`Remove aspect ${c.name}`}
                    disabled={locked}
                    onClick={() => void removeCriterion(c.id)}
                  />
                </Box>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mt: 4, flexWrap: 'wrap' }}>
              <TextField
                placeholder="Aspect name (e.g. Sustainability)"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                inputProps={{ 'aria-label': 'Aspect name' }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                placeholder="Short description (optional)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                inputProps={{ 'aria-label': 'Short description' }}
                sx={{ flex: 1, minWidth: 240 }}
              />
              <Button variant="tonal" onClick={() => void addCriterion()} disabled={locked}>
                Add
              </Button>
            </Box>
          </Section>
        ) : null}

        {tab === 'rdy' ? (
          <Section
            id="fc-rdy"
            title="Organizational Readiness"
            description="Describe the client's environment once. Every analysis reads it to score how readily the client can adopt each platform, on these three factors (everything else is already covered by the 8 capability criteria):"
          >
            <Box component="ol" sx={{ m: 0, mb: 4, pl: 5, '& li': { mb: 1.5 } }}>
              {(data.factors ?? []).map((f) => (
                <li key={f.code}>
                  <b>{f.name}</b> — {f.what}
                </li>
              ))}
            </Box>
            <FieldGroup
              title="Client profile"
              description="Mention: cloud & stack, in-house skills / production experience per vendor, and any enterprise agreements or preferred vendors."
            >
              <TextField
                multiline
                minRows={8}
                fullWidth
                disabled={locked}
                value={data.clientProfile}
                onChange={(e) => edit({ ...data, clientProfile: e.target.value })}
                inputProps={{ 'aria-label': 'Client profile' }}
                placeholder="e.g. Microsoft-centric enterprise: Microsoft 365, Azure and Active Directory across the estate, with a Microsoft Enterprise Agreement in place. Strong in-house .NET and Azure data engineering team; a small data-science group with OpenAI pilot experience. No AWS or GCP footprint or skills. Security requires all AI to run within the Azure tenant."
                sx={{ '& .MuiOutlinedInput-root': { height: 'auto', alignItems: 'flex-start' } }}
              />
            </FieldGroup>
          </Section>
        ) : null}

        {tab === 'wt' ? (
          <Section
            id="fc-wt"
            title="Weighting"
            description={
              <>
                How the two axes blend into the single <strong>Overall</strong> recommendation shown
                on the Fit and Summary screens.
              </>
            }
          >
            <Box sx={{ display: 'flex', gap: 8, mb: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 26,
                    lineHeight: '32px',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {wtCap}%
                </Typography>
                <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                  Platform capability
                </Typography>
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: 26,
                    lineHeight: '32px',
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {wtRdy}%
                </Typography>
                <Typography variant="body2" sx={{ color: WS_COLOR.textMuted }}>
                  Organizational readiness
                </Typography>
              </Box>
            </Box>
            <Slider
              min={0}
              max={100}
              step={5}
              disabled={locked}
              value={wtCap}
              onChange={(_, v) => edit({ ...data, weightCapability: v as number })}
              aria-label="Platform capability weight, percent"
              sx={{ maxWidth: 520 }}
            />
            <Typography variant="body2" sx={{ mt: 2 }}>
              Overall = {wtCap}% × Requirement&nbsp;Fit &nbsp;+&nbsp; {wtRdy}% ×
              Adoption&nbsp;Readiness
            </Typography>
          </Section>
        ) : null}
      </AdminLayout>

      <ConfirmDialog
        open={confirmUnlock}
        tone="destructive"
        title="Unlock the criteria?"
        confirmLabel="Unlock anyway"
        onClose={() => setConfirmUnlock(false)}
        onConfirm={() => {
          setConfirmUnlock(false);
          void doToggleLock();
        }}
      >
        AI Platform analysis already exists for {data.analyzedCount} opportunities. Unlock to change
        the criteria anyway? Existing analyses will not update.
      </ConfirmDialog>
    </>
  );
}
